<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import * as trainingDatasetApi from '@/services/rest-api-sms/lib/training-dataset/training-dataset'
import { getPercentage } from '@/helpers/calculator'

export default {
  page: {
    title: 'AI Training',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
  },

  mixins: [Helper],

  data() {
    return {
      isLoading: false,
      summary: {
        total_data: 0,
        total_valid_data: 0,
        total_invalid_data: 0,
        total_category_data: 0,
      },
      categories: [],
    }
  },

  created() {
    this.getTrainingDatasetSummary()
  },

  computed: {
    title() {
      return 'AI Training - Validasi Dataset'
    },

    summaryCards() {
      const validPercentage = getPercentage(this.summary.total_valid_data, this.summary.total_data)
      const invalidPercentage = getPercentage(this.summary.total_invalid_data, this.summary.total_data)
      return [
        {
          label: 'Total data',
          value: this.summary.total_data,
          helper: 'total data dari keseluruhan dataset',
          tone: 'primary',
        },
        {
          label: 'Valid',
          value: this.summary.total_valid_data,
          helper: `${validPercentage}% dari total data`,
          tone: 'success',
        },
        {
          label: 'Tidak valid',
          value: this.summary.total_invalid_data,
          helper: `${invalidPercentage}% dari total data`,
          tone: 'danger',
        },
        {
          label: 'Total SOP untuk divalidasi',
          value: this.summary.total_category_data,
          helper: 'total jumlah kategori dataset',
          tone: 'primary',
        },
      ]
    },
  },

  methods: {
    async getTrainingDatasetSummary() {
      this.isLoading = true

      const response = await trainingDatasetApi.getSummary().catch((error) => {
        this.handleError(error)
      })

      if (response?.status && response.data) {
        this.mapCategories(response)
      }

      this.isLoading = false
    },

    mapCategories(response) {
      this.summary = {
        total_data: response.data.total_data || 0,
        total_valid_data: response.data.total_valid_data || 0,
        total_invalid_data: response.data.total_invalid_data || 0,
        total_category_data: response.data.total_category_data || 0,
      }
      this.categories = response.data.categories || []
    },


    openValidation(category) {
      this.$router
        .push({
          path: '/work-order/ai-training/validation',
          query: { category: category },
        })
        .catch(() => {})
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <p class="mb-4">Summary data validasi dataset</p>

    <div class="ai-training-page">
      <b-row class="ai-training-summary">
        <b-col v-for="card in summaryCards" :key="card.label" cols="12" md="6" xl="3">
          <div class="card ai-training-summary__card">
            <div class="card-body">
              <p class="ai-training-summary__label mb-2">{{ card.label }}</p>
              <h3 class="mb-2" :class="`ai-training-summary__value--${card.tone}`">
                {{ card.value }}
              </h3>
              <p v-if="card.helper" class="ai-training-summary__helper mb-0">
                {{ card.helper }}
              </p>
            </div>
          </div>
        </b-col>
      </b-row>

      <div class="card ai-training-table mt-3">
        <div class="card-header ai-training-table__header">
          <h5 class="mb-0">Kategori dataset</h5>
          <span>Klik "Buka validasi" untuk mulai pada kategori tersebut</span>
        </div>

        <div class="table-responsive">
          <table class="table ai-training-table__content mb-0">
            <thead>
              <tr>
                <th>Kategori</th>
                <th class="text-nowrap">Total</th>
                <th>Valid</th>
                <th>Tidak valid</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading">
                <td colspan="5" class="text-center py-4">
                  <b-spinner small class="me-2"></b-spinner>
                  Memuat data...
                </td>
              </tr>
              <tr v-else-if="!categories.length">
                <td colspan="5" class="text-center text-muted py-4">
                  Data belum tersedia
                </td>
              </tr>
              <tr v-for="category in categories" :key="category.id">
                <td>
                  <div class="ai-training-category">
                    <div class="ai-training-category__icon">
                      <i class="bx bx-image"></i>
                    </div>
                    <div class="ai-training-category__content">
                      <strong>{{ category.category?.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) }}</strong>
                    </div>
                  </div>
                </td>
                <td class="ai-training-table__total">{{ category.total_data }} data</td>
                <td class="ai-training-table__valid">{{ category.total_valid_data }}</td>
                <td class="ai-training-table__invalid">{{ category.total_invalid_data }}</td>
                <td class="text-end">
                  <b-button variant="outline-primary" @click="openValidation(category.category)">
                    Buka validasi
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Layout>
</template>
